import { Grid, Paper, Typography } from '@material-ui/core';
import React from 'react';
import Modal from '../../modal';
import PaymentLimitsFees from '../../paymentus/payment-limits-fees';
import PGEButton from '../../buttons';
import { useStyles } from '../auto-pay-start/AutoPayStart.styles';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  AccountDetail,
  AccountType,
  PaymentCategory,
  PaymentProfile,
} from '../../../__generated__/pge-types';
import { AutoPayConfigData } from '../auto-pay.types';
import { mergeAutoPayConfigChanges } from '../auto-pay.utils';
import PaymentMethodSelector from '../../payment-wallet/payment-method-selector';
import { ADD_PROFILE } from '../../paymentus/types';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import AutoPayConfirm from '../auto-pay-confirm';
import { isIphoneChrome } from '../../../util/mobile/iphoneBrowser-validate';

type Props = {
  open: boolean;
  onClose: () => void;
  autoPayConfig: AutoPayConfigData;
  defaultAutoPayConfig: AutoPayConfigData;
  setAutoPayConfig: React.Dispatch<React.SetStateAction<AutoPayConfigData>>;
  account: AccountDetail;
  onUpdateAutoPayConfig: (config: AutoPayConfigData) => Promise<void>;
  setAutoNext: React.Dispatch<React.SetStateAction<boolean>>;
  currentPaymentProfile?: PaymentProfile | undefined;
  onAutoPayConfirmSubmit: () => void;
  onSubmit: () => void;
};

export default function AutoPayMethodChangeDialog({
  open,
  onClose,
  account,
  autoPayConfig,
  defaultAutoPayConfig,
  setAutoPayConfig,
  onUpdateAutoPayConfig,
  setAutoNext,
  currentPaymentProfile,
  onAutoPayConfirmSubmit,
  onSubmit,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);

  return (
    <Modal
      data-testid="payment-method-delete-confirm-modal"
      open={open}
      onClose={() => onClose()}
      children={
        !showConfirm ? (
          <>
            <Grid
              container
              direction={'column'}
              style={{
                width: 'auto',
                maxWidth: '800px',
                overflowY: 'auto',
                maxHeight: '90vh',
              }}
              spacing={4}
            >
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Typography variant="h3" style={{ marginBottom: '20px' }}>
                    {currentPaymentProfile?.type === PaymentCategory.AmazonPay
                      ? t('AMAZON_PAY_REMOVE_PAYMENT_PROFILE_TITLE')
                      : t('ADD_UPDATE_PAYMENT_METHOD')}
                  </Typography>
                </Grid>
                {currentPaymentProfile?.type === PaymentCategory.AmazonPay && (
                  <Grid item>
                    <Typography
                      variant="body1"
                      style={{ marginBottom: '20px' }}
                    >
                      {t(
                        'AUTO_PAY_CONFIG_UPDATE_AND_REMOVE_PAYMENT_PROFILE_DESC',
                      )}
                    </Typography>
                  </Grid>
                )}
                <Paper
                  style={{
                    justifyContent: 'flex-start',
                    padding: '10px 15px 20px 15px',
                  }}
                >
                  <Grid style={{ float: 'right', margin: '10px' }}>
                    <PaymentLimitsFees tabIndex={-1} />
                  </Grid>

                  <PaymentMethodSelector
                    selectLabel={t('SELECT_YOUR_PAYMENT_METHOD')}
                    isAutopay={true}
                    allowedCategories= {isIphoneChrome()
                      ? account?.autoPay?.availablePaymentMethods?.filter(
                          method => method !== PaymentCategory.Venmo,
                        )
                      : account?.autoPay?.availablePaymentMethods ?? undefined}
                    excludeCategories={
                      account?.accountType !== AccountType.Res
                        ? [
                            PaymentCategory.Cc,
                            PaymentCategory.Dc,
                            PaymentCategory.AmazonPay,
                            PaymentCategory.PaypalAccount,
                          ]
                        : account?.accountType === AccountType.Res &&
                          account?.paymentEligibility?.isCashOnly
                        ? [PaymentCategory.Dd]
                        : undefined
                    }
                    currentProfileInfo={
                      autoPayConfig.paymentSelector.selectedProfileInfo
                    }
                    onChange={selectedProfileInfo => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: config.paymentSelector.newProfileList,
                          selectedProfileInfo,
                        }),
                      );
                      onUpdateAutoPayConfig &&
                        onUpdateAutoPayConfig(autoPayConfig);
                    }}
                    onNew={newProfileInfo => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: [
                            ...config.paymentSelector.newProfileList,
                            newProfileInfo.profile,
                          ],
                          selectedProfileInfo: newProfileInfo,
                        }),
                      );
                      setAutoNext(true);
                    }}
                    onDelete={deleteProfile => {
                      setAutoPayConfig(config =>
                        mergeAutoPayConfigChanges(config, {
                          newProfileList: config.paymentSelector.newProfileList.filter(
                            p => p?.token !== deleteProfile?.profile?.token,
                          ),
                          selectedProfileInfo:
                            defaultAutoPayConfig.paymentSelector
                              .selectedProfileInfo,
                        }),
                      );
                      onUpdateAutoPayConfig &&
                        onUpdateAutoPayConfig(autoPayConfig);
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.actionArea}
              style={{ marginTop: '30px' }}
            >
              <PGEButton
                disabled={
                  !autoPayConfig?.paymentSelector?.selectedProfileInfo ||
                  autoPayConfig?.paymentSelector?.selectedProfileInfo?.profile
                    ?.token === currentPaymentProfile?.token ||
                  autoPayConfig?.paymentSelector?.selectedProfileInfo
                    ?.profile === ADD_PROFILE
                }
                onClick={() => {
                  setShowConfirm(true);
                  onSubmit();
                }}
              >
                {t('UPDATE_PAYMENT_PROFILE')}
              </PGEButton>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography variant="h3" style={{ marginBottom: '20px' }}>
                {currentPaymentProfile?.type === PaymentCategory.AmazonPay
                  ? t('AMAZON_PAY_REMOVE_PAYMENT_PROFILE_TITLE')
                  : t('ADD_UPDATE_PAYMENT_METHOD')}
              </Typography>
            </Grid>
            <AutoPayConfirm
              config={autoPayConfig}
              onSubmit={onAutoPayConfirmSubmit}
              fromBankUpdate
              disableCancelLink={true}
            />
          </>
        )
      }
    ></Modal>
  );
}
